import { useState, useEffect } from 'react';

function getIndiaDateTime() {
    const now = new Date();
    const options = {
        timeZone: 'Asia/Kolkata',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
    };
    const dateOptions = {
        timeZone: 'Asia/Kolkata',
        day: '2-digit',
        month: 'long',
        year: 'numeric',
    };
    const time = now.toLocaleTimeString('en-IN', options);
    const date = now.toLocaleDateString('en-IN', dateOptions);
    return `${time} · ${date}`;
}

export default function Topbar({ isDark, onToggleTheme, onLogout, user, operatorDetails, onNavigate, onToggleSidebar }) {
    const [dateTime, setDateTime] = useState(getIndiaDateTime());
    const [notifications, setNotifications] = useState([]);
    const [showNotifs, setShowNotifs] = useState(false);
    const [expiredDrivers, setExpiredDrivers] = useState([]);
    const [showExpiredModal, setShowExpiredModal] = useState(false);
    const isOperator = user?.userType === 'operator';
    const operatorId = user?.operatorId || (user?.userType === 'operator' ? user._id : null);

    const isSuperadmin = user?.userType === 'admin';

    const loadNotifications = () => {
        if (isSuperadmin) {
            fetch('/api/tickets')
                .then(r => r.json())
                .then(data => {
                    const superadminTickets = data.filter(t => t.tags?.includes('To Superadmin') && t.status?.name !== 'Closed' && t.status?.name !== 'Resolved');
                    if (superadminTickets.length > 0) {
                        setNotifications([{
                            _id: 'superadmin-tickets-notif',
                            isRead: false,
                            title: 'Support Tickets Awaiting Response',
                            message: `You have ${superadminTickets.length} open ticket(s) from operators.`,
                            createdAt: new Date().toISOString(),
                            type: 'superadmin_ticket'
                        }]);
                    } else {
                        setNotifications([]);
                    }
                })
                .catch(err => console.error('Error loading tickets for notifs:', err));
        } else if (operatorId) {
            fetch(`/api/notifications/operator/${operatorId}`)
                .then(r => r.json())
                .then(data => setNotifications(Array.isArray(data) ? data : []))
                .catch(err => console.error('Error loading notifications:', err));
        }

        // Load expired drivers, vehicles, and client contracts/documents
        if (operatorId || isSuperadmin) {
            const drvUrl = isSuperadmin ? `/api/drivers/all` : `/api/drivers/${operatorId}`;
            const vehUrl = isSuperadmin ? `/api/vehicles/${operatorId === null ? 'all' : operatorId}` : `/api/vehicles/${operatorId}`;
            const cliUrl = isSuperadmin ? `/api/clients/all` : `/api/clients/${operatorId}`;
            
            Promise.all([
                fetch(drvUrl).then(r => r.json()).catch(() => []),
                fetch(vehUrl).then(r => r.json()).catch(() => []),
                fetch(cliUrl).then(r => r.json()).catch(() => [])
            ]).then(([drivers, vehicles, clients]) => {
                const d = new Date();
                const todayStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
                
                const expired = [];
                
                if (Array.isArray(drivers)) {
                    drivers.forEach(driver => {
                        const expList = [];
                        if (driver.civilIdExpiry && String(driver.civilIdExpiry).split('T')[0] < todayStr) expList.push('Civil ID');
                        if (driver.passportExpiry && String(driver.passportExpiry).split('T')[0] < todayStr) expList.push('Passport');
                        if (driver.licenseExpiry && String(driver.licenseExpiry).split('T')[0] < todayStr) expList.push('Driving License');
                        
                        if (expList.length > 0) {
                            expired.push({ 
                                _id: driver._id, 
                                isVehicle: false,
                                isClient: false,
                                displayId: driver.driverCode || driver.driverId || '-',
                                displayName: driver.name || '-',
                                expiredList: expList 
                            });
                        }
                    });
                }
                
                if (Array.isArray(vehicles)) {
                    vehicles.forEach(vehicle => {
                        const expList = [];
                        if (vehicle.insuranceExpiry && String(vehicle.insuranceExpiry).split('T')[0] < todayStr) expList.push('Insurance');
                        if (vehicle.registrationExpiry && String(vehicle.registrationExpiry).split('T')[0] < todayStr) expList.push('Registration');
                        if (vehicle.vehicleLicenseExpiry && String(vehicle.vehicleLicenseExpiry).split('T')[0] < todayStr) expList.push('Vehicle License');
                        
                        if (expList.length > 0) {
                            expired.push({ 
                                _id: vehicle._id, 
                                isVehicle: true,
                                isClient: false,
                                displayId: vehicle.vehicleCode || vehicle.plateNumber || '-',
                                displayName: vehicle.make && vehicle.model ? `${vehicle.make} ${vehicle.model}` : vehicle.plateNumber,
                                expiredList: expList 
                            });
                        }
                    });
                }

                if (Array.isArray(clients)) {
                    clients.forEach(client => {
                        const expList = [];
                        if (Array.isArray(client.contracts)) {
                            client.contracts.forEach(c => {
                                if (c.endDate && String(c.endDate).split('T')[0] < todayStr) {
                                    expList.push(`Contract: ${c.title || 'Agreement'}`);
                                }
                            });
                        }
                        if (Array.isArray(client.documents)) {
                            client.documents.forEach(doc => {
                                if (doc.expiryDate && String(doc.expiryDate).split('T')[0] < todayStr) {
                                    expList.push(`Doc: ${doc.title || doc.type || 'Document'}`);
                                }
                            });
                        }
                        
                        if (expList.length > 0) {
                            expired.push({
                                _id: client._id,
                                isVehicle: false,
                                isClient: true,
                                displayId: client.companyName ? (client.companyName.length > 15 ? client.companyName.substring(0, 15) + '…' : client.companyName) : 'Client',
                                displayName: client.companyName || 'Corporate Client',
                                expiredList: expList
                            });
                        }
                    });
                }
                
                setExpiredDrivers(expired);
            }).catch(err => console.error('Error loading expiry data:', err));
        }
    };

    useEffect(() => {
        const timer = setInterval(() => {
            setDateTime(getIndiaDateTime());
        }, 1000);
        
        loadNotifications();
        const notifTimer = setInterval(loadNotifications, 30000);
        
        return () => {
            clearInterval(timer);
            clearInterval(notifTimer);
        };
    }, [operatorId]);

    const unreadCount = notifications.filter(n => !n.isRead).length;

    const markAllRead = async () => {
        if (!operatorId) return;
        try {
            await fetch(`/api/notifications/mark-all-read/operator/${operatorId}`, { method: 'PUT' });
            setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
        } catch (err) { console.error(err); }
    };

    const markRead = async (id) => {
        if (isSuperadmin) return; // Superadmin tickets notification is just a computed reflection, we don't 'mark read' permanently here.
        try {
            await fetch(`/api/notifications/${id}/read`, { method: 'PUT' });
            setNotifications(prev => prev.map(n => n._id === id ? { ...n, isRead: true } : n));
        } catch (err) { console.error(err); }
    };

    return (
        <header className={`topbar ${isOperator ? 'operator-topbar' : ''}`}>
            {/* ... previous topbar-left content ... */}
            <div className="topbar-left">
                <button className="topbar-icon-btn" onClick={onToggleSidebar} style={{ border: 'none', background: 'transparent', padding: '8px', marginRight: '8px', color: '#64748b', cursor: 'pointer' }} title="Toggle Sidebar">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="3" y1="12" x2="21" y2="12"></line><line x1="3" y1="6" x2="21" y2="6"></line><line x1="3" y1="18" x2="21" y2="18"></line></svg>
                </button>
                {!isOperator && (
                    <div className="topbar-meta">{dateTime}</div>
                )}
                {isOperator && (
                    <div className="topbar-back-btn">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"></polyline></svg>
                    </div>
                )}
            </div>



            <div className="topbar-actions" style={{ display: 'flex', alignItems: 'center' }}>
                {(isOperator || isSuperadmin) ? (
                    <>
                        {expiredDrivers.length > 0 && (
                            <button 
                                onClick={() => { setShowExpiredModal(false); if (onNavigate) onNavigate('expiry-command-center'); }}
                                style={{
                                    marginRight: 16,
                                    background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.08) 0%, rgba(220, 38, 38, 0.04) 100%)',
                                    color: '#dc2626',
                                    border: '1px solid rgba(239, 68, 68, 0.22)',
                                    padding: '6px 14px 6px 10px',
                                    borderRadius: 30,
                                    fontSize: 12.5,
                                    fontWeight: 700,
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 8,
                                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                    boxShadow: '0 2px 8px rgba(239, 68, 68, 0.06)',
                                    fontFamily: "'Outfit', sans-serif"
                                }}
                                onMouseEnter={(e) => { 
                                    e.currentTarget.style.background = '#fef2f2'; 
                                    e.currentTarget.style.borderColor = '#fca5a5'; 
                                    e.currentTarget.style.boxShadow = '0 4px 14px rgba(239, 68, 68, 0.15)'; 
                                    e.currentTarget.style.transform = 'translateY(-1px)'; 
                                }}
                                onMouseLeave={(e) => { 
                                    e.currentTarget.style.background = 'linear-gradient(135deg, rgba(239, 68, 68, 0.08) 0%, rgba(220, 38, 38, 0.04) 100%)'; 
                                    e.currentTarget.style.borderColor = 'rgba(239, 68, 68, 0.22)'; 
                                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(239, 68, 68, 0.06)'; 
                                    e.currentTarget.style.transform = 'translateY(0)'; 
                                }}
                                title="View Expiry Command Center"
                            >
                                <span style={{
                                    width: 24,
                                    height: 24,
                                    borderRadius: '50%',
                                    background: 'rgba(239, 68, 68, 0.12)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: '#dc2626'
                                }}>
                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path><line x1="12" y1="9" x2="12" y2="13"></line><line x1="12" y1="17" x2="12.01" y2="17"></line></svg>
                                </span>
                                <span>Expiry Center</span>
                                <span style={{
                                    background: '#ef4444',
                                    color: '#ffffff',
                                    fontSize: 11,
                                    fontWeight: 800,
                                    padding: '2px 7px',
                                    borderRadius: 12,
                                    lineHeight: 1,
                                    boxShadow: '0 1px 4px rgba(239, 68, 68, 0.35)'
                                }}>
                                    {expiredDrivers.length}
                                </span>
                            </button>
                        )}
                        <div 
                            className="topbar-icon-btn" 
                            style={{ position: 'relative', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                            onClick={() => setShowNotifs(!showNotifs)}
                        >
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#1e293b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
                                <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
                            </svg>
                            {unreadCount > 0 && (
                                <span style={{
                                    position: 'absolute', top: -2, right: -2, background: '#ef4444',
                                    color: 'white', fontSize: 10, fontWeight: 900, borderRadius: '50%',
                                    width: 18, height: 18, display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    border: '2px solid white'
                                }}>
                                    {unreadCount > 9 ? '9+' : unreadCount}
                                </span>
                            )}
                        </div>
                    </>
                ) : null}
            </div>

            {showNotifs && (
                <div style={{
                    position: 'absolute', top: 64, right: 20, width: 360, maxHeight: 500,
                    background: 'white', borderRadius: 16, boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
                    border: '1px solid #e2e8f0', zIndex: 9999, overflow: 'hidden', display: 'flex', flexDirection: 'column'
                }}>
                    <div style={{ padding: '16px 20px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f8fafc' }}>
                        <span style={{ fontWeight: 800, fontSize: 15, color: '#0f172a' }}>Notifications</span>
                        <button 
                            onClick={markAllRead}
                            style={{ background: 'none', border: 'none', color: '#16539C', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
                        >
                            Mark all read
                        </button>
                    </div>
                    <div style={{ flex: 1, overflowY: 'auto' }}>
                        {notifications.length === 0 ? (
                            <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>No notifications yet</div>
                        ) : notifications.map(n => (
                            <div 
                                key={n._id} 
                                onClick={() => {
                                    markRead(n._id);
                                    setShowNotifs(false);
                                    if (onNavigate) {
                                        if (n.type === 'superadmin_ticket') {
                                            onNavigate('tickets');
                                        } else if (n.type === 'allocation_updated') {
                                            onNavigate('passenger-allocation');
                                        } else if (n.type === 'support_ticket_created') {
                                            onNavigate('support-tickets');
                                        } else {
                                            onNavigate('operator-trip-list', 'All');
                                        }
                                    }
                                }}
                                style={{
                                    padding: '16px 20px', borderBottom: '1px solid #f1f5f9',
                                    background: n.isRead ? 'transparent' : '#f0f7ff',
                                    cursor: 'pointer', transition: 'background 0.2s'
                                }}
                            >
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                                    <span style={{ fontSize: 13, fontWeight: 700, color: n.isRead ? '#475569' : '#063A5C' }}>{n.title}</span>
                                    {!n.isRead && <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#16539C' }} />}
                                </div>
                                <div style={{ fontSize: 12, color: '#64748b', lineHeight: 1.5 }}>{n.message}</div>
                                <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 8 }}>{new Date(n.createdAt).toLocaleString()}</div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {showExpiredModal && (
                <div style={{
                    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999,
                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                }} onClick={() => setShowExpiredModal(false)}>
                    <div style={{
                        background: 'white', borderRadius: 16, padding: 24, width: '90%', maxWidth: 600,
                        maxHeight: '80vh', overflowY: 'auto', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
                    }} onClick={e => e.stopPropagation()}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                            <h2 style={{ margin: 0, fontSize: 20, color: '#ef4444', display: 'flex', alignItems: 'center', gap: 8 }}>
                                ⚠️ Expired Documents & Insurance
                            </h2>
                            <button onClick={() => setShowExpiredModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 24, color: '#64748b' }}>&times;</button>
                        </div>
                        {expiredDrivers.length === 0 ? (
                            <p style={{ color: '#64748b' }}>No expired items found.</p>
                        ) : (
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                                <thead>
                                    <tr style={{ background: '#f8fafc', textAlign: 'left' }}>
                                        <th style={{ padding: '12px', borderBottom: '1px solid #e2e8f0', color: '#475569', fontWeight: 600 }}>Type</th>
                                        <th style={{ padding: '12px', borderBottom: '1px solid #e2e8f0', color: '#475569', fontWeight: 600 }}>ID / Code</th>
                                        <th style={{ padding: '12px', borderBottom: '1px solid #e2e8f0', color: '#475569', fontWeight: 600 }}>Name / Details</th>
                                        <th style={{ padding: '12px', borderBottom: '1px solid #e2e8f0', color: '#475569', fontWeight: 600 }}>Expired Items</th>
                                        <th style={{ padding: '12px', borderBottom: '1px solid #e2e8f0', color: '#475569', fontWeight: 600 }}>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {expiredDrivers.map(d => (
                                        <tr key={d._id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                            <td style={{ padding: '12px' }}>
                                                <span style={{
                                                    fontSize: 11,
                                                    fontWeight: 700,
                                                    padding: '3px 8px',
                                                    borderRadius: 6,
                                                    background: d.isClient ? '#fef3c7' : d.isVehicle ? '#e0f2fe' : '#ede9fe',
                                                    color: d.isClient ? '#b45309' : d.isVehicle ? '#0369a1' : '#6d28d9'
                                                }}>
                                                    {d.isClient ? 'Client' : d.isVehicle ? 'Vehicle' : 'Driver'}
                                                </span>
                                            </td>
                                            <td style={{ padding: '12px', fontWeight: 600, color: '#334155' }}>{d.displayId}</td>
                                            <td style={{ padding: '12px', color: '#334155', fontWeight: 600 }}>{d.displayName}</td>
                                            <td style={{ padding: '12px', color: '#ef4444', fontWeight: 600 }}>{d.expiredList.join(', ')}</td>
                                            <td style={{ padding: '12px' }}>
                                                <button 
                                                    onClick={() => {
                                                        setShowExpiredModal(false);
                                                        if (onNavigate) {
                                                            if (d.isClient) {
                                                                onNavigate('client-details', d._id);
                                                            } else {
                                                                onNavigate(d.isVehicle ? 'all-vehicles' : 'drivers', d._id);
                                                            }
                                                        }
                                                    }}
                                                    style={{ background: '#2563eb', color: 'white', border: 'none', padding: '5px 12px', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
                                                >
                                                    View
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            )}
        </header>
    );
}
